import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Download, Pause, Play, RotateCcw } from "lucide-react";
import { useBackend } from "../../backend/BackendProvider";
import type { Attachment } from "../../domain/entities";
import { attachmentKind, isPreviewableAttachment } from "../../domain/attachments";
import { Dialog } from "../Dialog";
import { TaskNotesMarkdown } from "../TaskNotesMarkdown";

export function AttachmentPreview({ attachments, initialId, onClose, onDownload }: {
  attachments: Attachment[];
  initialId: string | null;
  onClose: () => void;
  onDownload: (attachment: Attachment) => void;
}) {
  const backend = useBackend();
  const previewable = useMemo(() => attachments.filter(isPreviewableAttachment), [attachments]);
  const [index, setIndex] = useState(() => Math.max(0, previewable.findIndex((attachment) => attachment.id === initialId)));
  const [source, setSource] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [mode, setMode] = useState<"rendered" | "source">("rendered");
  const [zoom, setZoom] = useState(1);
  const [actual, setActual] = useState(false);
  const [paused, setPaused] = useState(() => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const imageRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const attachment = previewable[index];

  useEffect(() => { if (initialId) setIndex(Math.max(0, previewable.findIndex((item) => item.id === initialId))); }, [initialId, previewable]);
  useEffect(() => {
    if (!attachment) return;
    let cancelled = false;
    let url: string | null = null;
    setLoading(true); setError(null); setSource(null); setImageUrl(null); setMode("rendered"); setZoom(1); setActual(false);
    void backend.attachments.download(attachment.id).then(async (blob) => {
      if (cancelled) return;
      if (attachmentKind(attachment) === "markdown") setSource(await blob.text());
      else { url = typeof URL.createObjectURL === "function" ? URL.createObjectURL(blob) : null; setImageUrl(url); }
      setLoading(false);
    }).catch((reason: unknown) => { if (!cancelled) { setError(reason instanceof Error ? reason.message : "The preview could not be loaded."); setLoading(false); } });
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [attachment, attempt, backend]);
  useEffect(() => () => { if (imageUrl) URL.revokeObjectURL(imageUrl); }, [imageUrl]);

  if (!attachment) return null;
  const kind = attachmentKind(attachment);
  const isGif = attachment.originalName.toLocaleLowerCase("en-US").endsWith(".gif");
  const move = (direction: -1 | 1) => setIndex((current) => Math.max(0, Math.min(previewable.length - 1, current + direction)));
  const pause = () => {
    const image = imageRef.current; const canvas = canvasRef.current;
    if (image && canvas) { canvas.width = image.naturalWidth; canvas.height = image.naturalHeight; canvas.getContext("2d")?.drawImage(image, 0, 0); }
    setPaused(true);
  };

  return <Dialog open title={`${attachment.originalName} · ${index + 1} of ${previewable.length}`} onClose={onClose} footer={<div className="attachment-preview-footer"><button type="button" className="button small" onClick={() => onDownload(attachment)}><Download size={14} />Download</button>{previewable.length > 1 && <div className="button-group"><button type="button" className="button small" aria-label="Previous preview" disabled={index === 0} onClick={() => move(-1)}><ChevronLeft size={14} />Previous</button><button type="button" className="button small" aria-label="Next preview" disabled={index === previewable.length - 1} onClick={() => move(1)}>Next<ChevronRight size={14} /></button></div>}</div>}>
    <div className="attachment-preview" aria-label={`Preview ${attachment.originalName}`} data-attachment-preview>
      {loading && <p role="status">Loading preview…</p>}
      {error && <div className="attachment-preview-error" role="alert"><span>{error}</span><button type="button" className="button small" onClick={() => setAttempt((value) => value + 1)}>Retry</button></div>}
      {kind === "image" && !error && <>
        <div className="attachment-preview-toolbar" role="toolbar" aria-label="Image controls">
          <button type="button" className="button small" onClick={() => { setActual(false); setZoom(1); }}>Fit to window</button>
          <button type="button" className="button small" onClick={() => { setActual(true); setZoom(1); }}>Actual size</button>
          <button type="button" className="button small" aria-label="Zoom out" disabled={zoom <= .25} onClick={() => { setActual(false); setZoom((value) => Math.max(.25, value - .25)); }}>−</button>
          <span aria-live="polite">{Math.round(zoom * 100)}%</span>
          <button type="button" className="button small" aria-label="Zoom in" disabled={zoom >= 4} onClick={() => { setActual(false); setZoom((value) => Math.min(4, value + .25)); }}>+</button>
          <button type="button" className="button small" onClick={() => { setActual(false); setZoom(1); }}><RotateCcw size={13} />Reset</button>
          {isGif && <button type="button" className="button small" onClick={() => paused ? setPaused(false) : pause()}>{paused ? <><Play size={13} />Play</> : <><Pause size={13} />Pause</>}</button>}
        </div>
        <div className={`attachment-preview-canvas${actual ? " actual" : ""}`}>
          {imageUrl && <img ref={imageRef} src={imageUrl} alt={attachment.originalName} onLoad={() => { if (isGif && paused) pause(); }} style={actual ? undefined : { transform: `scale(${zoom})` }} />}
          {imageUrl && isGif && paused && <canvas ref={canvasRef} className="attachment-paused-frame" aria-label={`Paused frame of ${attachment.originalName}`} />}
        </div>
      </>}
      {kind === "markdown" && !error && <>
        <div className="segmented attachment-preview-tabs" role="tablist" aria-label="Markdown view">
          <button type="button" role="tab" aria-selected={mode === "rendered"} className={mode === "rendered" ? "active" : ""} onClick={() => setMode("rendered")}>Rendered</button>
          <button type="button" role="tab" aria-selected={mode === "source"} className={mode === "source" ? "active" : ""} onClick={() => setMode("source")}>Source</button>
        </div>
        {mode === "rendered" ? <div className="attachment-markdown"><TaskNotesMarkdown source={source ?? ""} label={`Attachment ${attachment.originalName}`} /></div> : <pre className="attachment-source" aria-label="Markdown source">{source ?? ""}</pre>}
      </>}
    </div>
  </Dialog>;
}
